<template>
  <div class="mb-40 min-h-[calc(100vh-120px)] md:mb-24">
    <div class="mx-auto my-10 mt-16 px-6">
      <StepProgress
        class="mb-10"
        :titles="['Search', 'Select Roles', 'Estimate Hours', 'See Candidates']"
        :current-step="newServiceSelectionSteps.indexOf(currentStep) + 1"
      ></StepProgress>
      <div v-if="currentStep === 'requirements'">
        <h1 class="mx-auto my-0 max-w-4xl text-center text-2xl font-bold">
          Welcome to Virtual Gurus Talentplace!
        </h1>
        <p class="mx-auto max-w-3xl text-center">
          Let’s find the right assistant(s) for your business. Just tell us a
          bit about what you need, and you’ll see your candidates in just a few
          clicks.
        </p>

        <div
          class="mx-auto my-5 max-w-4xl rounded-2xl border-2 bg-white pb-5 shadow-md"
        >
          <KeywordSearch :error="requirementsError">
            <template #title>
              <h1 class="mb-1 text-2xl">
                Start searching for roles, skills or tools that you need help
                with
              </h1>
              <p class="mb-3 text-sm">
                Add as many requirements as you like - the more the better
              </p>
            </template>
          </KeywordSearch>
          <word-selector :title="`Your Requirements`"> </word-selector>
        </div>
        <SuggestedKeywords />
      </div>
      <div v-else-if="currentStep === 'roles'" class="mx-auto my-0">
        <Roles :error="rolesError" />
      </div>
      <div v-else-if="currentStep === 'hours'">
        <HoursSelector :error="hoursError" />
      </div>
    </div>
    <div
      class="mx-auto my-0 mb-6 flex w-full max-w-4xl flex-col-reverse justify-end gap-4 px-5 md:flex-row"
    >
      <vg-btn
        :class="{
          invisible: newServiceSelectionSteps.indexOf(currentStep) === 0,
        }"
        :disabled="shouldDisableBack"
        @click="goBack"
      >
        Back
      </vg-btn>

      <vg-btn
        :disabled="shouldDisableNext"
        @click="goNext"
        @click:disabled="GoNextDisabled"
      >
        Next
      </vg-btn>
    </div>
  </div>
</template>
<script>
import { mapStores, storeToRefs } from 'pinia';
import { useClientDashboardStore } from '@/stores/clientDashboard';
import { useCheckoutStore } from '@/stores/checkout';
import { useUserStore } from '@/stores/user';
import { sessionUserHasPermission } from '@/services/clientSessionService';
import { saveProject } from '@/services/projectService';
import KeywordSearch from '@/components/serviceSelection/KeywordSearch.vue';
import WordSelector from '@/components/serviceSelection/WordSelector.vue';
import Roles from '@/components/serviceSelection/Roles.vue';
import HoursSelector from '@/components/serviceSelection/HoursSelector.vue';
import StepProgress from '@/components/serviceSelection/StepProgress.vue';
import SuggestedKeywords from '@/components/serviceSelection/SuggestedKeywords.vue';

export default {
  name: 'TalentPlaceSearch',
  components: {
    KeywordSearch,
    WordSelector,
    Roles,
    HoursSelector,
    StepProgress,
    SuggestedKeywords,
  },
  emits: ['goNext', 'goBack'],
  data() {
    const { user } = storeToRefs(useUserStore());
    const { initClientProjects } = useClientDashboardStore();

    return {
      user,
      initClientProjects,
      newServiceSelectionSteps: [
        'requirements',
        'roles',
        'hours',
        'candidates',
      ],
      showRequirementsError: false,
      showRolesError: false,
      showHoursError: false,
    };
  },

  computed: {
    ...mapStores(useCheckoutStore),
    currentStep: function () {
      return this.$route.query.step || 'requirements';
    },

    shouldDisableBack: function () {
      return this.$route.query.step === 'requirements';
    },
    shouldDisableNext() {
      if (this.currentStep === 'requirements') {
        return (
          this.checkoutStore.flattedEnteredKeywords.length === 0 &&
          this.checkoutStore.selectedRoles.length === 0
        );
      } else if (this.currentStep === 'roles') {
        return this.checkoutStore.selectedRoles.length === 0;
      } else if (this.currentStep === 'hours') {
        return (
          this.checkoutStore.matchingCriteria?.hours?.length === 0 ||
          this.checkoutStore.matchingCriteria?.hours?.filter((hours) => !!hours)
            .length !== this.checkoutStore.matchingCriteria?.rids?.length
        );
      }
      return false;
    },
    requirementsError() {
      return (
        this.showRequirementsError === true &&
        this.checkoutStore.flattedEnteredKeywords.length === 0 &&
        this.currentStep === 'requirements'
      );
    },
    rolesError() {
      return (
        this.currentStep === 'roles' &&
        this.checkoutStore.selectedRoles.length === 0 &&
        this.showRolesError === true
      );
    },
    hoursError() {
      return this.currentStep === 'hours' && this.showHoursError === true;
    },
  },
  watch: {
    'checkoutStore.flattedEnteredKeywords'() {
      if (this.checkoutStore.flattedEnteredKeywords.length > 0) {
        this.showRequirementsError = false;
      }
    },
    'checkoutStore.selectedRoles'() {
      if (this.checkoutStore.selectedRoles.length > 0) {
        this.showRolesError = false;
      }
    },
  },

  methods: {
    GoNextDisabled() {
      if (
        this.checkoutStore.flattedEnteredKeywords.length === 0 &&
        this.currentStep === 'requirements'
      ) {
        this.showRequirementsError = true;
      } else if (
        this.currentStep === 'roles' &&
        this.checkoutStore.selectedRoles.length === 0
      ) {
        this.showRolesError = true;
      } else if (this.currentStep === 'hours') {
        this.showHoursError =
          !this.checkoutStore.matchingCriteria?.hours?.length ||
          this.checkoutStore.matchingCriteria?.hours.filter((hours) => !!hours)
            .length !== this.checkoutStore.matchingCriteria?.rids.length;
      }
    },

    async goNext() {
      this.showRequirementsError = this.showRolesError = this.showHoursError = false;

      const nextIndex =
        this.newServiceSelectionSteps.indexOf(this.currentStep) + 1;

      if (this.currentStep === 'requirements') {
        if (
          this.checkoutStore.flattedEnteredKeywords.length === 0 &&
          this.checkoutStore.selectedRoles.length === 0
        ) {
          this.$root.$snackbar.error(
            'Please type in the search box and select a keyword to proceed'
          );
          return;
        }
        let newProjectUuid;
        if (
          !this.checkoutStore.matchingCriteria.uuid &&
          this.user &&
          sessionUserHasPermission('client')
        ) {
          newProjectUuid = await saveProject().then(
            (response) => response.project_uuid
          );

          await this.initClientProjects({ forceRefresh: true });
        }

        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          step: this.newServiceSelectionSteps[nextIndex],
          uuid: newProjectUuid ?? this.checkoutStore.matchingCriteria.uuid,
        });
      } else if (this.currentStep === 'roles') {
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          rids:
            this.checkoutStore.selectedRoles.length > 0
              ? this.checkoutStore.selectedRoles.map((role) => role.id)
              : [],
          step: this.newServiceSelectionSteps[nextIndex],
        });
      } else if (this.currentStep === 'hours') {
        this.showHoursError =
          this.checkoutStore.matchingCriteria?.hours?.length === 0 ||
          this.checkoutStore.matchingCriteria?.hours?.filter((hours) => !!hours)
            .length !== this.checkoutStore.matchingCriteria?.rids.length;

        if (this.showHoursError) {
          return;
        }
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          step: this.newServiceSelectionSteps[nextIndex],
        });
      }
      if (this.currentStep !== 'candidates') {
        this.$emit('goNext');
      }
      this.scrollToTop();
    },
    async goBack() {
      this.showRequirementsError = this.showRolesError = this.showHoursError = false;
      const nextIndex =
        this.newServiceSelectionSteps.indexOf(this.currentStep) - 1;

      if (nextIndex !== -1) {
        if (this.currentStep === 'roles') {
          await this.checkoutStore.updateMatchingCriteria({
            ...this.checkoutStore.matchingCriteria,
            step: this.newServiceSelectionSteps[nextIndex],
          });
        } else if (this.currentStep === 'hours') {
          await this.checkoutStore.updateMatchingCriteria({
            ...this.checkoutStore.matchingCriteria,
            step: this.newServiceSelectionSteps[nextIndex],
          });
        } else if (this.currentStep === 'candidates') {
          await this.checkoutStore.updateMatchingCriteria({
            ...this.checkoutStore.matchingCriteria,
            step: this.newServiceSelectionSteps[nextIndex],
          });
        }
      }
      this.$emit('goBack');
      this.scrollToTop();
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
};
</script>
